import type { Networked3dWebExperienceClient } from "@mml-io/3d-web-experience-client";

// -- Types -------------------------------------------------------------------

type PlacementRecord = {
  id: string;
  src: string;
  x: number;
  y: number;
  z: number;
  rx: number;
  ry: number;
  rz: number;
  placedByWallet: string;
  createdAt: string;
};

type LibraryItem = { id: string; name: string; publicUrl: string };

type InputMode = "library" | "url" | "code";

/** Reserved `m-frame` id for live placement preview (must not collide with server placement ids). */
const PREVIEW_FRAME_ID = "spot-mml-placement-preview";

// -- Auth helper (mirrors index.ts) ------------------------------------------

function getSpotAuthToken(): string | null {
  try {
    const raw = localStorage.getItem("spot-auth");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.accessToken || !parsed.expiresAt) return null;
    if (Date.now() >= new Date(parsed.expiresAt).getTime() - 60_000) return null;
    return parsed.accessToken;
  } catch {
    return null;
  }
}

// -- API calls ---------------------------------------------------------------

const KT_API_URL = (window as any).KT_API_URL || "";

async function fetchLibraryMmls(): Promise<LibraryItem[]> {
  const token = getSpotAuthToken();
  if (!token || !KT_API_URL) return [];
  try {
    const res = await fetch(`${KT_API_URL}/api/mmls?limit=100`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return [];
    const data = await res.json();
    return (data.mmls || []).map((m: any) => ({
      id: m.id,
      name: m.name,
      publicUrl: m.publicUrl,
    }));
  } catch {
    return [];
  }
}

async function fetchPlacements(): Promise<PlacementRecord[]> {
  try {
    const res = await fetch("/api/world/placements", { credentials: "include" });
    if (!res.ok) return [];
    const data = await res.json();
    return data.placements ?? [];
  } catch {
    return [];
  }
}

async function postPlacement(body: {
  src: string;
  x: number;
  y: number;
  z: number;
  rx?: number;
  ry?: number;
  rz?: number;
}): Promise<PlacementRecord | null> {
  try {
    const res = await fetch("/api/world/placements", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

async function deletePlacement(id: string): Promise<boolean> {
  try {
    const res = await fetch(`/api/world/placements/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    return res.ok;
  } catch {
    return false;
  }
}

async function postEphemeralMml(content: string): Promise<string | null> {
  try {
    const res = await fetch("/api/world/placements/ephemeral-mml", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ content }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.url ?? null;
  } catch {
    return null;
  }
}

// -- DOM helpers -------------------------------------------------------------

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: Record<string, string>,
  children?: (Node | string)[],
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (k === "className") e.className = v;
      else e.setAttribute(k, v);
    }
  }
  if (children) {
    for (const c of children) e.append(typeof c === "string" ? document.createTextNode(c) : c);
  }
  return e;
}

// -- Main placement system ---------------------------------------------------

export function initMmlObjectPlacement(
  app: Networked3dWebExperienceClient & {
    addPlacementFrame: (id: string, spec: any) => void;
    updatePlacementFrame: (id: string, spec: any) => void;
    removePlacementFrame: (id: string) => void;
    raycastWorldPointFromClientXY: (x: number, y: number) => any;
    getHolderElement: () => HTMLElement;
    ensurePreviewMarker: () => void;
    /** Smoothed world position of the placement preview (matches what you see). Use for final placement. */
    getPlacementPreviewWorldPosition: () => { x: number; y: number; z: number } | null;
    showPreviewMarker: (x: number, y: number, z: number) => void;
    hidePreviewMarker: () => void;
    getLocalCharacterPosition: () => { x: number; y: number; z: number } | null;
  },
) {
  // State
  let panelVisible = false;
  let placementModeActive = false;
  let resolvedSrc: string | null = null;
  let currentInputMode: InputMode = "library";
  let libraryItems: LibraryItem[] = [];
  let libraryLoaded = false;
  let ownPlacements: PlacementRecord[] = [];
  /** True after the preview `m-frame` has been created for the current placement session. */
  let previewMmlFrameMounted = false;
  /** Primary "Place in World" button in the panel (re-created when `renderPanel` runs). */
  let placeSubmitButton: HTMLButtonElement | null = null;

  function syncPlaceSubmitDisabled() {
    if (placeSubmitButton) {
      placeSubmitButton.disabled = !resolvedSrc;
    }
  }

  const holderElement = app.getHolderElement();

  // ── FAB button ────────────────────────────────────────────────────────────
  const fab = el("button", { className: "spot-mml-fab", type: "button" });
  fab.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="22" height="22"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>`;
  fab.title = "Place MML Object";
  holderElement.appendChild(fab);

  // ── Panel ─────────────────────────────────────────────────────────────────
  const panel = el("div", { className: "spot-mml-panel" });
  panel.style.display = "none";
  holderElement.appendChild(panel);

  // ── Placement instruction banner ──────────────────────────────────────────
  const banner = el("div", { className: "spot-mml-banner" });
  banner.textContent = "Left-click to place · Esc to cancel";
  banner.style.display = "none";
  holderElement.appendChild(banner);

  // ── Panel rendering ───────────────────────────────────────────────────────

  function renderPanel() {
    panel.innerHTML = "";

    // Header
    const header = el("div", { className: "spot-mml-panel-header" }, [
      el("span", { className: "spot-mml-panel-title" }, ["MML Objects"]),
    ]);
    const closeBtn = el("button", {
      className: "spot-mml-panel-close",
      type: "button",
    }, ["✕"]);
    closeBtn.addEventListener("click", () => togglePanel(false));
    header.appendChild(closeBtn);
    panel.appendChild(header);

    // Tabs
    const tabs = el("div", { className: "spot-mml-tabs" });
    for (const mode of ["library", "url", "code"] as InputMode[]) {
      const label = mode === "library" ? "Library" : mode === "url" ? "MML URL" : "MML Code";
      const tab = el("button", {
        className: `spot-mml-tab ${currentInputMode === mode ? "spot-mml-tab--active" : ""}`,
        type: "button",
      }, [label]);
      tab.addEventListener("click", () => {
        currentInputMode = mode;
        if (mode === "library" && !libraryLoaded) loadLibrary();
        renderPanel();
      });
      tabs.appendChild(tab);
    }
    panel.appendChild(tabs);

    // Input area
    const inputArea = el("div", { className: "spot-mml-input-area" });

    if (currentInputMode === "library") {
      if (!libraryLoaded) {
        inputArea.appendChild(el("div", { className: "spot-mml-status" }, ["Loading library…"]));
        loadLibrary();
      } else if (libraryItems.length === 0) {
        inputArea.appendChild(
          el("div", { className: "spot-mml-status" }, ["No MMLs in your library"]),
        );
      } else {
        const select = el("select", { className: "spot-mml-select" }) as HTMLSelectElement;
        select.appendChild(el("option", { value: "", disabled: "true", selected: "true" }, ["Select an MML…"]));
        for (const item of libraryItems) {
          select.appendChild(el("option", { value: item.publicUrl }, [item.name]));
        }
        if (resolvedSrc) {
          select.value = resolvedSrc;
        }
        select.addEventListener("change", () => {
          resolvedSrc = select.value || null;
          syncPlaceSubmitDisabled();
        });
        inputArea.appendChild(select);
      }
    } else if (currentInputMode === "url") {
      const input = el("input", {
        className: "spot-mml-input",
        type: "text",
        placeholder: "https://… or wss://…",
      }) as HTMLInputElement;
      input.addEventListener("input", () => {
        resolvedSrc = input.value.trim() || null;
        syncPlaceSubmitDisabled();
      });
      inputArea.appendChild(input);
    } else {
      const textarea = el("textarea", {
        className: "spot-mml-textarea",
        placeholder: '<m-cube color="red"></m-cube>',
        rows: "6",
      }) as HTMLTextAreaElement;
      const prepareBtn = el("button", {
        className: "spot-mml-btn spot-mml-btn--secondary",
        type: "button",
      }, ["Prepare"]);
      const codeStatus = el("div", { className: "spot-mml-code-status" });
      prepareBtn.addEventListener("click", async () => {
        const code = textarea.value.trim();
        if (!code) return;
        codeStatus.textContent = "Uploading…";
        const url = await postEphemeralMml(code);
        if (url) {
          resolvedSrc = url;
          codeStatus.textContent = "Ready to place!";
          codeStatus.className = "spot-mml-code-status spot-mml-code-status--ok";
        } else {
          resolvedSrc = null;
          codeStatus.textContent = "Failed to prepare MML";
          codeStatus.className = "spot-mml-code-status spot-mml-code-status--err";
        }
        syncPlaceSubmitDisabled();
      });
      inputArea.appendChild(textarea);
      inputArea.appendChild(el("div", { className: "spot-mml-code-actions" }, [prepareBtn, codeStatus]));
    }
    panel.appendChild(inputArea);

    // Place button
    const placeBtn = el("button", {
      className: "spot-mml-btn spot-mml-btn--primary",
      type: "button",
    }, ["Place in World"]);
    placeSubmitButton = placeBtn;
    syncPlaceSubmitDisabled();
    placeBtn.addEventListener("click", () => {
      if (!resolvedSrc) return;
      enterPlacementMode(resolvedSrc);
    });
    panel.appendChild(placeBtn);

    // Divider
    panel.appendChild(el("hr", { className: "spot-mml-divider" }));

    // My placements
    const myHeader = el("div", { className: "spot-mml-section-header" }, ["My Placements"]);
    panel.appendChild(myHeader);

    if (ownPlacements.length === 0) {
      panel.appendChild(el("div", { className: "spot-mml-status" }, ["No placements yet"]));
    } else {
      const list = el("div", { className: "spot-mml-placement-list" });
      for (const p of ownPlacements) {
        const row = el("div", { className: "spot-mml-placement-row" });
        const label = el("span", { className: "spot-mml-placement-label" }, [
          truncate(p.src, 35),
        ]);
        const removeBtn = el("button", {
          className: "spot-mml-btn spot-mml-btn--danger spot-mml-btn--sm",
          type: "button",
        }, ["Remove"]);
        removeBtn.addEventListener("click", async () => {
          const ok = await deletePlacement(p.id);
          if (ok) {
            app.removePlacementFrame(p.id);
            ownPlacements = ownPlacements.filter((r) => r.id !== p.id);
            renderPanel();
          }
        });
        row.appendChild(label);
        row.appendChild(removeBtn);
        list.appendChild(row);
      }
      panel.appendChild(list);
    }
  }

  function truncate(s: string, max: number): string {
    return s.length <= max ? s : s.slice(0, max - 1) + "…";
  }

  // ── Library loader ────────────────────────────────────────────────────────

  async function loadLibrary() {
    if (libraryLoaded) return;
    libraryItems = await fetchLibraryMmls();
    libraryLoaded = true;
    if (panelVisible) renderPanel();
  }

  // ── Toggle panel ──────────────────────────────────────────────────────────

  function togglePanel(show?: boolean) {
    panelVisible = show ?? !panelVisible;
    panel.style.display = panelVisible ? "flex" : "none";
    if (panelVisible) {
      if (currentInputMode === "library" && !libraryLoaded) loadLibrary();
      renderPanel();
    }
  }

  fab.addEventListener("click", (e) => {
    e.stopPropagation();
    togglePanel();
  });

  // ── Placement mode ────────────────────────────────────────────────────────

  function getCanvasCenterClientXY(): { x: number; y: number } | null {
    const canvas = holderElement.querySelector("canvas");
    if (!canvas) return null;
    const r = canvas.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }

  function updatePreviewAtClient(clientX: number, clientY: number) {
    if (!placementModeActive || !resolvedSrc) return;
    const point = app.raycastWorldPointFromClientXY(clientX, clientY);
    if (!point) return;
    app.ensurePreviewMarker();
    // Only set `src` once. Re-applying `url` on every move re-triggers m-frame load and looks like scaling/resizing.
    if (!previewMmlFrameMounted) {
      app.addPlacementFrame(PREVIEW_FRAME_ID, {
        url: resolvedSrc,
        x: point.x,
        y: point.y,
        z: point.z,
        sx: 1,
        sy: 1,
        sz: 1,
      });
      previewMmlFrameMounted = true;
    } else {
      app.updatePlacementFrame(PREVIEW_FRAME_ID, {
        x: point.x,
        y: point.y,
        z: point.z,
        sx: 1,
        sy: 1,
        sz: 1,
      });
    }
  }

  function enterPlacementMode(src: string) {
    if (placementModeActive) return;
    placementModeActive = true;
    resolvedSrc = src;
    togglePanel(false);
    banner.style.display = "flex";

    // Capture phase: VirtualJoystick attaches pointermove on document and calls
    // stopPropagation(), which blocks bubble listeners on window — so placement
    // must run in capture phase to receive every move over the canvas.
    window.addEventListener("pointermove", onPointerMove, { capture: true });
    window.addEventListener("pointerdown", onPlacementPlacePointerDown, { capture: true });
    window.addEventListener("keydown", onPlacementKey);

    const center = getCanvasCenterClientXY();
    if (center) {
      updatePreviewAtClient(center.x, center.y);
    }
  }

  function exitPlacementMode() {
    if (!placementModeActive) return;
    placementModeActive = false;
    previewMmlFrameMounted = false;
    banner.style.display = "none";
    app.removePlacementFrame(PREVIEW_FRAME_ID);
    app.hidePreviewMarker();

    window.removeEventListener("pointermove", onPointerMove, { capture: true });
    window.removeEventListener("pointerdown", onPlacementPlacePointerDown, { capture: true });
    window.removeEventListener("keydown", onPlacementKey);
  }

  function onPointerMove(e: PointerEvent) {
    if (!placementModeActive) return;
    updatePreviewAtClient(e.clientX, e.clientY);
  }

  async function onPlacementPlacePointerDown(e: PointerEvent) {
    if (!placementModeActive || !resolvedSrc) return;
    if (e.button !== 0) return;

    e.preventDefault();
    e.stopPropagation();

    const target = e.target as HTMLElement;
    if (target.closest(".spot-mml-fab, .spot-mml-panel, .spot-mml-banner")) return;

    const previewPos = app.getPlacementPreviewWorldPosition();
    const point =
      previewPos ??
      app.raycastWorldPointFromClientXY(e.clientX, e.clientY);
    if (!point) return;

    const src = resolvedSrc;
    exitPlacementMode();

    const record = await postPlacement({
      src,
      x: point.x,
      y: point.y,
      z: point.z,
    });

    if (record) {
      ownPlacements.push(record);
    }
  }

  function onPlacementKey(e: KeyboardEvent) {
    if (e.key === "Escape") {
      exitPlacementMode();
    }
  }

  // ── Broadcast handler (called from index.ts) ─────────────────────────────

  function handleBroadcast(broadcast: { broadcastType: string; payload: any }) {
    if (broadcast.broadcastType === "spot_placement_add") {
      const p = broadcast.payload as PlacementRecord;
      app.addPlacementFrame(p.id, {
        url: p.src,
        x: p.x,
        y: p.y,
        z: p.z,
        rx: p.rx,
        ry: p.ry,
        rz: p.rz,
      });
    } else if (broadcast.broadcastType === "spot_placement_remove") {
      const { id } = broadcast.payload;
      app.removePlacementFrame(id);
      ownPlacements = ownPlacements.filter((r) => r.id !== id);
    }
  }

  // ── Proximity removal (R key) ──────────────────────────────────────────────

  const PROXIMITY_RANGE = 4;
  let nearbyPlacementId: string | null = null;

  const proximityPrompt = el("div", { className: "spot-mml-proximity-prompt" });
  proximityPrompt.innerHTML = `<span class="spot-mml-proximity-key">R</span> Remove object`;
  proximityPrompt.style.display = "none";
  holderElement.appendChild(proximityPrompt);

  function distXZ(
    a: { x: number; z: number },
    b: { x: number; z: number },
  ): number {
    const dx = a.x - b.x;
    const dz = a.z - b.z;
    return Math.sqrt(dx * dx + dz * dz);
  }

  function checkProximity() {
    if (placementModeActive || ownPlacements.length === 0) {
      if (nearbyPlacementId) {
        nearbyPlacementId = null;
        proximityPrompt.style.display = "none";
      }
      return;
    }

    const pos = app.getLocalCharacterPosition();
    if (!pos) return;

    let closestId: string | null = null;
    let closestDist = PROXIMITY_RANGE;

    for (const p of ownPlacements) {
      const d = distXZ(pos, p);
      if (d < closestDist) {
        closestDist = d;
        closestId = p.id;
      }
    }

    if (closestId !== nearbyPlacementId) {
      nearbyPlacementId = closestId;
      proximityPrompt.style.display = closestId ? "flex" : "none";
    }
  }

  setInterval(checkProximity, 250);

  window.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "r" || e.key === "R") {
      if (!nearbyPlacementId || placementModeActive) return;
      // Don't trigger if user is typing in an input
      const active = document.activeElement;
      if (
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active instanceof HTMLSelectElement
      ) return;

      const id = nearbyPlacementId;
      nearbyPlacementId = null;
      proximityPrompt.style.display = "none";

      deletePlacement(id).then((ok) => {
        if (ok) {
          app.removePlacementFrame(id);
          ownPlacements = ownPlacements.filter((r) => r.id !== id);
          if (panelVisible) renderPanel();
        }
      });
    }
  });

  // ── Initial hydration ─────────────────────────────────────────────────────

  async function hydrate() {
    const all = await fetchPlacements();
    const authRaw = localStorage.getItem("spot-auth");
    let myWallet: string | null = null;
    try {
      myWallet = authRaw ? JSON.parse(authRaw).wallet?.toLowerCase() : null;
    } catch { /* ignore */ }

    for (const p of all) {
      app.addPlacementFrame(p.id, {
        url: p.src,
        x: p.x,
        y: p.y,
        z: p.z,
        rx: p.rx,
        ry: p.ry,
        rz: p.rz,
      });
      if (myWallet && p.placedByWallet === myWallet) {
        ownPlacements.push(p);
      }
    }
  }

  // Kick off hydration
  hydrate();

  return { handleBroadcast };
}
